/* <version-picker> — accessible dropdown for the product page version selector.
   DOM comes from snippets/scs-productpage-versions.liquid, styles from
   scs-version-picker.css.

   Click the trigger to open (focus lands on the selected option, else first).
   Click an option to navigate to its data-url; click outside to close without
   navigating. Keyboard: up/down move focus, Home/End jump to first/last,
   Enter/Space activate, Escape closes and refocuses the trigger, Tab closes. */

class VersionPicker extends HTMLElement {
  #trigger;
  #listbox;
  #options;
  #focusedIndex;
  #boundOnDocumentClick;

  connectedCallback() {
    this.#trigger = this.querySelector('.version-picker__trigger');
    this.#listbox = this.querySelector('.version-picker__listbox');
    this.#options = Array.from(this.querySelectorAll('.version-picker__option'));
    this.#focusedIndex = -1;

    if (!this.#trigger || !this.#listbox || this.#options.length === 0) {
      return;
    }

    this.#boundOnDocumentClick = this.#onDocumentClick.bind(this);

    this.#trigger.addEventListener('click', this.#onTriggerClick.bind(this));
    this.#trigger.addEventListener('keydown', this.#onTriggerKeydown.bind(this));
    this.#listbox.addEventListener('keydown', this.#onListboxKeydown.bind(this));
    this.#options.forEach((option) => {
      option.addEventListener('click', this.#onOptionClick.bind(this));
    });
  }

  disconnectedCallback() {
    if (this.#boundOnDocumentClick) {
      document.removeEventListener('click', this.#boundOnDocumentClick);
    }
  }

  get #isOpen() {
    return this.#trigger.getAttribute('aria-expanded') === 'true';
  }

  #open() {
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#listbox.hidden = false;
    document.addEventListener('click', this.#boundOnDocumentClick);

    const selectedIndex = this.#options.findIndex((opt) =>
      opt.classList.contains('version-picker__option--selected')
    );
    this.#focusOption(selectedIndex >= 0 ? selectedIndex : 0);
  }

  #close({ returnFocus = false } = {}) {
    if (!this.#isOpen) return;

    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#listbox.hidden = true;

    if (this.#focusedIndex >= 0 && this.#options[this.#focusedIndex]) {
      this.#options[this.#focusedIndex].blur();
    }
    this.#focusedIndex = -1;
    this.#options.forEach((opt) => opt.setAttribute('tabindex', '-1'));

    document.removeEventListener('click', this.#boundOnDocumentClick);

    if (returnFocus) {
      this.#trigger.focus();
    }
  }

  #focusOption(index) {
    if (index < 0 || index >= this.#options.length) return;

    this.#options.forEach((opt, i) => {
      opt.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    this.#focusedIndex = index;
    this.#options[index].focus();
  }

  #activateOption(index) {
    const option = this.#options[index];
    if (!option) return;
    const url = option.dataset.url;
    if (!url) return;
    window.location.href = url;
  }

  #onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.#isOpen) {
      this.#close({ returnFocus: true });
    } else {
      this.#open();
    }
  }

  #onTriggerKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.#isOpen) this.#open();
        break;
    }
  }

  #onListboxKeydown(event) {
    if (!this.#isOpen) return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const next = Math.min(this.#focusedIndex + 1, this.#options.length - 1);
        this.#focusOption(next);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const prev = Math.max(this.#focusedIndex - 1, 0);
        this.#focusOption(prev);
        break;
      }
      case 'Home':
        event.preventDefault();
        this.#focusOption(0);
        break;
      case 'End':
        event.preventDefault();
        this.#focusOption(this.#options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#activateOption(this.#focusedIndex);
        break;
      case 'Escape':
      case 'Esc':
        event.preventDefault();
        this.#close({ returnFocus: true });
        break;
      case 'Tab':
        this.#close();
        break;
    }
  }

  #onOptionClick(event) {
    event.preventDefault();
    const option = event.currentTarget;
    const index = this.#options.indexOf(option);
    if (index >= 0) this.#activateOption(index);
  }

  #onDocumentClick(event) {
    if (this.#isOpen && !this.contains(event.target)) {
      this.#close();
    }
  }
}

if (!customElements.get('version-picker')) {
  customElements.define('version-picker', VersionPicker);
}
